import type { ActionArtKey } from './registry';
import type { ActionArtRuntime } from './runtime';

const FRAME_SIZE_PX = 512;
const STRIP_HEIGHT_PX = 3072;
const FRAME_COUNT = 6;

export interface ActionStripDomImageSource {
  readonly src: string;
}

export interface ActionStripDomActor {
  readonly wrapper: HTMLDivElement;
  readonly frames: readonly HTMLDivElement[];
  readonly destroyed: boolean;
  showFrame(frame: number, crossfadeMs?: number): void;
  setTransform(transform: string): void;
  abort(): void;
  destroy(): void;
}

export interface ActionStripDomActorOptions<TImage extends ActionStripDomImageSource> {
  readonly runtime: ActionArtRuntime<TImage>;
  readonly cueToken: string | number;
  readonly key: ActionArtKey;
  readonly host: HTMLElement;
  readonly signal?: AbortSignal;
}

export function createActionStripDomActor<TImage extends ActionStripDomImageSource>(
  options: ActionStripDomActorOptions<TImage>,
): ActionStripDomActor | null {
  if (options.signal?.aborted === true) {
    options.runtime.abortPending();
    return null;
  }

  const status = options.runtime.lookupForCue(options.cueToken, options.key);
  if (
    status.status !== 'ready'
    || status.image.src.length === 0
    || status.asset.width !== FRAME_SIZE_PX
    || status.asset.height !== STRIP_HEIGHT_PX
    || status.asset.frameWidth !== FRAME_SIZE_PX
    || status.asset.frameHeight !== FRAME_SIZE_PX
    || status.asset.frameCount !== FRAME_COUNT
  ) {
    return null;
  }
  const pin = status.acquirePin();

  const wrapper = document.createElement('div');
  wrapper.dataset.actionArtActor = 'strip';
  wrapper.dataset.actionArtCueToken = String(options.cueToken);
  wrapper.dataset.actionArtKey = options.key;
  wrapper.setAttribute('aria-hidden', 'true');
  wrapper.setAttribute('inert', '');
  wrapper.inert = true;
  wrapper.style.position = 'relative';
  wrapper.style.width = `${FRAME_SIZE_PX}px`;
  wrapper.style.height = `${FRAME_SIZE_PX}px`;
  wrapper.style.pointerEvents = 'none';
  wrapper.style.transformOrigin = 'center center';

  const frames = Array.from({ length: FRAME_COUNT }, (_unused, frameIndex) => {
    const frame = document.createElement('div');
    frame.dataset.actionArtFrame = String(frameIndex);
    frame.dataset.actionArtActive = frameIndex === 0 ? 'true' : 'false';
    frame.setAttribute('aria-hidden', 'true');
    frame.style.position = 'absolute';
    frame.style.inset = '0px';
    frame.style.width = `${FRAME_SIZE_PX}px`;
    frame.style.height = `${FRAME_SIZE_PX}px`;
    frame.style.overflow = 'hidden';
    frame.style.backgroundImage = `url("${escapeCssUrl(status.image.src)}")`;
    frame.style.backgroundPosition = `0px -${frameIndex * FRAME_SIZE_PX}px`;
    frame.style.backgroundRepeat = 'no-repeat';
    frame.style.backgroundSize = `${FRAME_SIZE_PX}px ${STRIP_HEIGHT_PX}px`;
    frame.style.opacity = frameIndex === 0 ? '1' : '0';
    frame.style.transitionProperty = 'opacity';
    frame.style.transitionDuration = '0ms';
    wrapper.append(frame);
    return frame;
  });
  const readonlyFrames = Object.freeze([...frames]);
  let destroyed = false;
  let initialFrameCommitted = false;

  function showFrame(frameIndex: number, crossfadeMs = 0): void {
    if (!Number.isInteger(frameIndex) || frameIndex < 0 || frameIndex >= FRAME_COUNT) {
      throw new RangeError(`Action strip frame must be an integer from 0 to ${FRAME_COUNT - 1}.`);
    }
    if (!Number.isFinite(crossfadeMs) || crossfadeMs < 0) {
      throw new RangeError('Action strip crossfade duration must be a non-negative finite number.');
    }
    if (destroyed) return;

    if (!initialFrameCommitted) {
      // The first frame and its opacity must enter the rendered style tree
      // before we switch to the next pose; otherwise browsers may collapse
      // both writes into one style update and never paint the authored ready pose.
      wrapper.getBoundingClientRect();
      initialFrameCommitted = true;
    }

    const transitionDuration = `${crossfadeMs}ms`;
    for (const [index, frame] of frames.entries()) {
      const active = index === frameIndex;
      frame.dataset.actionArtActive = String(active);
      frame.style.transitionDuration = transitionDuration;
      frame.style.opacity = active ? '1' : '0';
    }
  }

  function setTransform(transform: string): void {
    if (destroyed) return;
    wrapper.style.transform = transform;
  }

  function destroy(): void {
    if (destroyed) return;
    destroyed = true;
    options.signal?.removeEventListener('abort', abort);
    for (const frame of frames) {
      frame.style.backgroundImage = '';
      frame.style.transitionDuration = '';
    }
    wrapper.replaceChildren();
    wrapper.remove();
    pin.release();
  }

  function abort(): void {
    if (destroyed) return;
    options.runtime.abortPending();
    destroy();
  }

  const actor: ActionStripDomActor = Object.freeze({
    wrapper,
    frames: readonlyFrames,
    get destroyed() {
      return destroyed;
    },
    showFrame,
    setTransform,
    abort,
    destroy,
  });

  options.signal?.addEventListener('abort', abort, { once: true });
  options.host.append(wrapper);
  return actor;
}

function escapeCssUrl(url: string): string {
  return url
    .replaceAll('\\', '\\\\')
    .replaceAll('"', '\\"')
    .replaceAll('\n', '\\a ')
    .replaceAll('\r', '\\d ');
}
